import { QUESTION_TYPES } from 'chameleon-src/constants/assignment';

const developmentThresholds = {
  DEVELOPING: 0.4,
  APPROACHING: 0.75,
  ACHIEVING: 1,
};

const getReduceFunction =
  (questionDataset, questionType, property = 'score') =>
  (total, item) => {
    let question = questionDataset[item.accNum];
    if (
      question &&
      (questionType ? question.questionType === questionType : true)
    ) {
      return total + item[property];
    }
    return total;
  };

const aggregation = ({
  studentDataset,
  questionDataset,
  classAveragePoints,
  totalAssessmentPoints,
  assignments,
}) => {
  try {
    const studentIds = studentDataset ? Object.keys(studentDataset) : null;
    const questionIds = questionDataset ? Object.keys(questionDataset) : null;

    const questionsByAssignment = assignments.reduce(
      (curr, next) => [
        ...curr,
        {
          assignmentId: next.id,
          questions: next.questionTagsById.map((q) => q.id),
        },
      ],
      [],
    );

    const questionsData = [];
    const studentData = [];

    if (studentIds && studentIds.length > 0 && questionDataset) {
      // find a student that has the most items (meaning they've taken both parts of a two part exam if this was indeed one of those)
      let lastKnownMaxItemCount = 0;

      studentIds.forEach((id) => {
        const student = studentDataset[id];
        const itemCount = Object.keys(student).length;
        if (student && itemCount > lastKnownMaxItemCount) {
          lastKnownMaxItemCount = itemCount;
        }
      });

      questionIds.forEach((questionId) => {
        const question = questionDataset[questionId];
        const {
          id,
          accNum,
          questionType,
          assessmentGroupSortOrder,
          tags,
          totalPoints,
        } = question;
        const students = {};
        let correctAnswersGiven = 0;
        let correctAnswersScore = 0;

        // const questionOrder = index + 1;
        studentIds.forEach((studentId) => {
          const student = studentDataset[studentId];

          if (student.items[questionId].correct) {
            correctAnswersGiven++;
          }
          correctAnswersScore += student.items[questionId].score;

          students[studentId] = {
            correctAnswersGiven: student.items[questionId].correct ? 1 : 0,
            correctAnswersScore: student.items[questionId].score || 0,
            attempted: student.items[questionId].attempted,
          };
        });
        const questionAssignment = questionsByAssignment.find((a) =>
          a.questions.includes(question.id),
        );

        let questionData = {
          id,
          title: `Question ${assessmentGroupSortOrder + 1}`,
          order: assessmentGroupSortOrder,
          accNum,
          // many legacy places expect `learnosityItemReference`, but the graphql response uses `accNum`.
          learnosityItemReference: accNum,
          questionType,
          // PerformanceItem and some others expect `type` but the graphql response calls this `questionType`.
          type: questionType,
          tags,
          correctAnswersGiven,
          correctAnswersScore,
          totalPoints,
          students,
          assignmentId: questionAssignment && questionAssignment.assignmentId,
        };

        questionsData.push(questionData);
      });

      studentIds.forEach((studentId) => {
        const student = studentDataset[studentId];
        const items = student.items
          ? Object.keys(student.items).map((itemId) =>
              Object.assign({ accNum: itemId }, student.items[itemId]),
            )
          : [];

        studentData.push({
          id: studentId,
          name: student.metadata.name,
          lastName: student.metadata.last_name,
          firstName: student.metadata.first_name,
          pointsTotal: items.reduce(getReduceFunction(questionDataset), 0),
          pointsMCQ: items.reduce(
            getReduceFunction(questionDataset, QUESTION_TYPES.multipleChoice),
            0,
          ),
          pointsFRQ: items.reduce(
            getReduceFunction(questionDataset, QUESTION_TYPES.freeResponse),
            0,
          ),
        });
      });

      // sort it based on the server's ideas of what sorting means. (stable sort)
      questionsData.sort((a, b) => {
        if (a.order === b.order) return 0;
        return a.order < b.order ? -1 : 1;
      });
    }

    const studentDeveloping = (sd) =>
      sd.pointsTotal / totalAssessmentPoints < developmentThresholds.DEVELOPING;
    const studentApproaching = (sd) =>
      sd.pointsTotal / totalAssessmentPoints >=
        developmentThresholds.DEVELOPING &&
      sd.pointsTotal / totalAssessmentPoints <
        developmentThresholds.APPROACHING;
    const studentAchieving = (sd) =>
      sd.pointsTotal / totalAssessmentPoints >=
      developmentThresholds.APPROACHING;

    const developingStudentsCount =
      studentData.filter(studentDeveloping).length;
    const approachingStudentsCount =
      studentData.filter(studentApproaching).length;
    const achievingStudentsCount = studentData.filter(studentAchieving).length;

    const developingStudentsPercentage =
      (developingStudentsCount / studentData.length) * 100;
    const approachingStudentsPercentage =
      (approachingStudentsCount / studentData.length) * 100;
    const achievingStudentsPercentage =
      (achievingStudentsCount / studentData.length) * 100;

    return {
      data: {
        questionsData,
        studentData,
        classAverage: classAveragePoints,
        developingStudentsPercentage,
        approachingStudentsPercentage,
        achievingStudentsPercentage,
        pointsAvailable: totalAssessmentPoints,
      },
      error: null,
    };
  } catch (error) {
    return { error, data: null };
  }
};

export default aggregation;
